import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '../../contexts/AuthContext';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { STEP_META } from '../../fixtures/onboarding';
import {
  chapterAriaLive,
  chapterByNumber,
  chapterKicker,
  type ChapterProgress,
} from '../../lib/onboardingChapters';
import type { StepNumber } from '../../hooks/onboardingReducer';
import { TayroMark } from '../brand/TayroMark';
import { scrollRouteToTop } from '../../lib/scrollRouteToTop';
import { ChapterSegments } from './ChapterSegments';
import { OnboardingShellV2 } from './OnboardingShellV2';
import { OnboardingV2Provider, useOnboardingV2 } from './onboardingV2';
import styles from './OnboardingShell.module.css';

/** True when the document is scrolled down from the top. */
function pageIsScrolled(): boolean {
  if (typeof document === 'undefined') return false;
  const scroller = document.scrollingElement ?? document.documentElement;
  return (scroller?.scrollTop ?? 0) > 0 || (document.body?.scrollTop ?? 0) > 0;
}

export type OnboardingShellProps = {
  current: StepNumber;
  /** Back-button handler. Pass undefined to hide the button (e.g. on
   *  Step 1, which has no back). */
  onBack?: () => void;
  /** Whether to display the "Optional" badge in the top row. */
  optional?: boolean;
  /** Skip-link config. Pass null/undefined to hide. */
  skip?: { label: string; onClick: () => void };
  /** Step body — the question + options + CTA. The shell owns the
   *  ambient panel chrome (logo, step label, serif title, context). */
  children: ReactNode;
  /** Bare layout — used by Step 7 Welcome to skip the standard chrome
   *  and render full-bleed on the welcome ambient bg. */
  bare?: boolean;
  /** P7(a) — non-step screens (the step-0 role fork, the coach team-setup
   *  flow) wear the SAME split-panel chrome but aren't numbered athlete
   *  steps: this replaces STEP_META and the "Step N of 6" label, and the
   *  progress rail is suppressed. */
  metaOverride?: { stepLabel: string; label: string; context: string; ambientBg: string };
  /**
   * O1 — where this screen sits in the four-chapter flow, computed by the route
   * from the screens ACTUALLY queued for this athlete.
   *
   * Presence is what turns the chapter treatment on:
   *   · given      → "Chapter N of 4" kicker, chapter name as the serif h1,
   *                  chapter context line, chapter ambient, the four segments,
   *                  and the chaptered aria-live sentence.
   *   · omitted    → no segments at all. That is the DOORSTEP rule (age step,
   *                  account fork, coach setup) and the onboarding-lite rule:
   *                  both keep the chrome they already had, through
   *                  `metaOverride`.
   *
   * `metaOverride` still wins for the panel COPY when both are passed, which is
   * how the guardian beat keeps its own ratified framing while still showing
   * chapter 4's segments.
   */
  chapter?: ChapterProgress;
  /** B215 — true while a child screen has an irrevocable request in flight
   *  (currently only the age step's under-13 removal) that a sign-out must
   *  not abandon mid-flight. Disables the control without hiding it; every
   *  other mount omits this and Sign out is unaffected. */
  signOutDisabled?: boolean;
  /**
   * Part 7 (flag `onboarding_2_0_v1`, read only by the restyled shell) — this
   * screen prints its own serif headline, so the header keeps the kicker and the
   * context line and the name moves into the page as the one `<h1>`. Used by the
   * parent beat and the short coach chapter's intro. Ignored with the flag off.
   */
  headerNameInBody?: boolean;
};

/**
 * OnboardingShell — top-level layout for the onboarding flow. Dispatches
 * between desktop two-column (≥1024px: ambient left + scrollable
 * right) and mobile single-column (ambient strip + scrollable
 * content).
 *
 * ── O1, the chaptered pass (CD handoff `design_handoff_onboarding_chapters`,
 * founder-ratified 2026-08-30) ────────────────────────────────────────────
 * The ambient panel no longer names a STEP. It names a CHAPTER: the kicker
 * reads "Chapter N of 4", the serif `<h1>` is the chapter name, and the context
 * line is the chapter's. The step's own question stays in the body `<h2>`
 * (`StepHeading`), exactly as before — that is what makes a chapter turn read
 * as a turn rather than as an interstitial, and why there are no interstitial
 * screens: the panel simply crossfades to the next ambient (320ms, the
 * transition the panel already had) and retitles.
 *
 * The six-dot `ProgressRail` retires FROM THIS FLOW in favour of
 * `ChapterSegments`. The component itself is untouched and still exported for
 * its other consumer (`routes/primitives.tsx`).
 *
 * The serif chapter name receives programmatic focus on change for screen
 * reader navigation; the aria-live region announces
 * "Chapter N of 4, {chapter name}, question X of Y" — replacing the old
 * "Step N of 7 · {name}".
 *
 * For Step 7 Welcome, pass `bare` to skip the standard chrome and let
 * the welcome step component render its own centered layout on the
 * chapter 4 ambient color.
 */
function OnboardingShellClassic({
  current,
  onBack,
  optional,
  skip,
  children,
  bare,
  metaOverride,
  chapter,
  signOutDisabled,
}: OnboardingShellProps) {
  const isDesktop = useMediaQuery('(min-width: 1024px)');
  const chapterMeta = chapter ? chapterByNumber(chapter.n) : null;
  // Copy resolution, in priority order:
  //   1. metaOverride — an explicitly framed screen (doorstep, guardian beat,
  //      coach setup, onboarding-lite). Wins even when a chapter is present.
  //   2. the chapter — every ordinary chaptered question.
  //   3. STEP_META — defensive only; no route path reaches it.
  const meta = metaOverride ??
    (chapterMeta
      ? {
          stepLabel: chapterKicker(chapterMeta.n),
          label: chapterMeta.name,
          context: chapterMeta.context,
          ambientBg: chapterMeta.ambientBg,
        }
      : { ...STEP_META[current - 1], stepLabel: '' });
  // The chaptered sentence replaces "Step N of 7". Without a chapter there is
  // no honest number to quote (the doorstep is not step 1 of anything), so the
  // screen's own name is announced alone.
  const liveMessage = chapter ? chapterAriaLive(chapter) : meta.label;
  const headingRef = useRef<HTMLHeadingElement | null>(null);

  // B6 (2026-05-16) — sign-out from the onboarding flow. User flagged
  // 2026-05-16: "I cannot sign out unless I do the onboarding again."
  // Without an escape hatch, a user wrongly routed to /v2/onboarding
  // (e.g., via the legacy-data gap fixed elsewhere in this PR) had no
  // way out except completing or abandoning the browser tab.
  const { signOut, user: authUser } = useAuthContext();
  const navigate = useNavigate();
  const handleSignOut = async () => {
    try {
      await signOut();
    } catch {
      /* Supabase clears local state regardless */
    }
    navigate('/landing');
  };

  // B215 — a removal in flight (currently only the age step's under-13
  // removal) must not be abandoned by signing out mid-request: same gate as
  // the stop's own Back/Cancel/confirm, reported up through `signOutDisabled`.
  const handleSignOutClick = () => {
    if (signOutDisabled) return;
    void handleSignOut();
  };

  // Programmatic focus on the panel heading when the screen changes — for
  // screen reader navigation. (Phase 1 dashboard had no equivalent because
  // it wasn't a multi-step flow.)
  //
  // O1: keyed on the announced sentence rather than on `current` alone. Two of
  // the chaptered screens share a wizard step number with their neighbour (the
  // name screen opens chapter 1 ahead of step 1; the universal pace screen sits
  // inside step 6 ahead of the focus question), so `current` no longer changes
  // on every screen change and cannot be the whole trigger.
  //
  // B465 (walk 2026-10-22) — "some steps open scrolled about 64px". `focus()` on
  // a heading scrolls it just into view, so a screen reached from the bottom of a
  // long one landed with its top row (Back, Sign out) above the fold. A new
  // screen now starts at the top of the page, and the focus move no longer
  // scrolls on its own. Not on the first mount: the document is already at the top
  // there, and a reset would be a write nobody asked for.
  const focusedOnceRef = useRef(false);
  useEffect(() => {
    if (bare) return;
    // Only when the page really is scrolled: an ordinary forward pass from the top
    // of a short screen moves the page for nobody.
    if (focusedOnceRef.current && pageIsScrolled()) scrollRouteToTop();
    focusedOnceRef.current = true;
    headingRef.current?.focus({ preventScroll: true });
  }, [current, bare, liveMessage]);

  // Bare variant — Step 7 Welcome owns its own layout.
  if (bare) {
    return (
      <div
        className={styles.bare}
        style={{ '--ambient-bg': meta.ambientBg } as CSSProperties & Record<string, string>}
      >
        <div className={styles.srOnly} aria-live="polite">
          {liveMessage}
        </div>
        {children}
      </div>
    );
  }

  const ambientStyle: CSSProperties & Record<string, string> = {
    '--ambient-bg': meta.ambientBg,
  };

  // ─────────────────────────────────────────────────────────────────────
  // Top-row controls — back button + optional/skip
  // ─────────────────────────────────────────────────────────────────────
  const topRow = (
    <>
      <button
        type="button"
        className={styles.backBtn}
        onClick={onBack}
        aria-disabled={!onBack || undefined}
        // B472 — with no `onBack` this is only a placeholder that keeps the top row's
        // shape (the stylesheet hides it); it is out of the accessibility tree and the
        // tab order too, so the first screen does not offer a Back that goes nowhere.
        aria-hidden={!onBack || undefined}
        tabIndex={!onBack ? -1 : undefined}
        aria-label="Go back to previous step"
      >
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
          <path
            d="M9 11L5 7l4-4"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        Back
      </button>

      {/* Right cluster — optional badge + skip link + sign-out
          (any subset). Sign-out only renders when an auth user is
          present, so this whole component is unaffected when used in
          test/fixture contexts that don't mount AuthProvider. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        {/* O1 — the mobile dot strip retires with ProgressRail. Per the
            handoff the segments now live at the BOTTOM OF THE AMBIENT STRIP on
            mobile, not in this row, so the top row is controls only. */}
        {optional && <span className={styles.optionalBadge}>Optional</span>}
        {skip && (
          <button
            type="button"
            className={styles.skipLink}
            onClick={skip.onClick}
          >
            {skip.label}
          </button>
        )}
        {authUser && (
          <button
            type="button"
            className={styles.signOutLink}
            onClick={handleSignOutClick}
            disabled={signOutDisabled}
            aria-disabled={signOutDisabled || undefined}
            aria-label="Sign out"
            // B215 — same opacity rule the under-13 stop's own controls use
            // (`AgeStep.module.css` `:disabled`), inline here rather than in
            // the shell's CSS module (outside this lane's ownership).
            style={signOutDisabled ? { opacity: 0.6, cursor: 'default' } : undefined}
          >
            Sign out
          </button>
        )}
      </div>
    </>
  );

  // ─────────────────────────────────────────────────────────────────────
  // Ambient panel content — shared between desktop left + mobile top
  // ─────────────────────────────────────────────────────────────────────
  const ambientContent = (
    <>
      <div className={styles.leftLogo}>
        <TayroMark size={24} />
        <span>Tayro</span>
      </div>
      <div className={styles.leftMiddle}>
        <div className={styles.stepLabel}>{meta.stepLabel}</div>
        {/* `key` on the title is what replays the 200ms/8px chapter-turn rise:
            it remounts when the chapter name changes and stays put between
            questions inside one chapter. */}
        <h1 className={styles.stepName} tabIndex={-1} ref={headingRef} key={meta.label}>
          {meta.label}
        </h1>
        <p className={styles.contextLine}>{meta.context}</p>
      </div>
    </>
  );

  // ─────────────────────────────────────────────────────────────────────
  // Desktop layout
  // ─────────────────────────────────────────────────────────────────────
  if (isDesktop) {
    return (
      <div className={styles.wrap}>
        <div className={styles.srOnly} aria-live="polite">
          {liveMessage}
        </div>

        <div className={styles.desktop}>
          <aside className={styles.leftPanel} style={ambientStyle}>
            {ambientContent}
            <div className={styles.leftBottom}>
              {chapter && <ChapterSegments progress={chapter} />}
            </div>
          </aside>

          <main className={styles.rightPanel}>
            <div className={styles.rightTopRow}>{topRow}</div>
            <div className={styles.rightContent}>
              <div className={styles.rightContentInner}>{children}</div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────────────
  // Mobile layout
  // ─────────────────────────────────────────────────────────────────────
  return (
    <div className={styles.wrap}>
      <div className={styles.srOnly} aria-live="polite">
        {liveMessage}
      </div>

      <div className={styles.mobile}>
        <div className={styles.mobileAmbient} style={ambientStyle}>
          {ambientContent}
          {chapter && (
            <div className={styles.mobileSegments}>
              <ChapterSegments progress={chapter} />
            </div>
          )}
        </div>

        <div className={styles.mobileTopRow}>{topRow}</div>

        <main className={styles.mobileContent}>{children}</main>
      </div>
    </div>
  );
}

/** The shell for one held answer: the restyle only when it is on and the screen is not the bare reveal. */
function HeldShell({ v2, ...props }: OnboardingShellProps & { v2: boolean }) {
  if (v2 && !props.bare) return <OnboardingShellV2 {...props} />;
  return <OnboardingShellClassic {...props} />;
}

/**
 * The shell, behind `onboarding_2_0_v1` (Part 7). The flag is read once by the
 * route and arrives as context (`useOnboardingV2`); with it off, or on the bare
 * variant the reveal wears, this is the shell that shipped, untouched, above.
 * The restyled shell is a sibling component, so neither tree can drift into the
 * other.
 *
 * B489 — the answer is held for the length of a screen. The flag reads false until
 * `/api/config` lands (a cold load with no cached config), and a swap from one shell
 * to the other under a screen she is filling in unmounted everything below it, so a
 * name half typed was gone. The shell keeps the answer it started the screen with; a
 * new screen (another question, chapter, framed screen, or the bare reveal) takes
 * the answer as it stands. What is inside reads the held answer too, so a screen is
 * never half one look and half the other.
 */
export function OnboardingShell(props: OnboardingShellProps) {
  const live = useOnboardingV2();
  const screen = [
    props.current,
    props.chapter?.n ?? '',
    props.chapter?.questionIndex ?? '',
    props.metaOverride?.label ?? '',
    props.bare ? 'bare' : '',
  ].join('|');
  const [held, setHeld] = useState({ screen, v2: live });
  let v2 = held.v2;
  if (held.screen !== screen) {
    v2 = live;
    setHeld({ screen, v2: live });
  }
  return (
    <OnboardingV2Provider value={v2}>
      <HeldShell v2={v2} {...props} />
    </OnboardingV2Provider>
  );
}
